import { defineStore } from 'pinia'

const STORAGE_KEY = 'deliveriesFilters'

interface IDeliveriesFilters {
  tatIDs: string[]
  tierIDs: string[]
  insuranceCustomerAccountIDs: string[]
  onlyReady: boolean
}

const DEFAULT_FILTERS: IDeliveriesFilters = {
  tatIDs: [],
  tierIDs: [],
  insuranceCustomerAccountIDs: [],
  onlyReady: false,
}

export const useDeliveriesFilterStore = defineStore('deliveriesFilter', {
  state: () => ({
    tatIDs: [] as string[],
    tierIDs: [] as string[],
    insuranceCustomerAccountIDs: [] as string[],
    onlyReady: false,
    isLoaded: false,
  }),

  getters: {
    hasActiveFilters(state): boolean {
      return (
        state.tatIDs.length > 0 ||
        state.tierIDs.length > 0 ||
        state.insuranceCustomerAccountIDs.length > 0 ||
        state.onlyReady
      )
    },
  },

  actions: {
    loadFilters() {
      if (this.isLoaded) return
      this.isLoaded = true

      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const parsed: unknown = JSON.parse(stored)
          if (parsed && typeof parsed === 'object') {
            const { tatIDs, tierIDs, insuranceCustomerAccountIDs, onlyReady } =
              parsed as Partial<IDeliveriesFilters>
            this.tatIDs = Array.isArray(tatIDs)
              ? tatIDs.filter((x): x is string => typeof x === 'string')
              : []
            this.tierIDs = Array.isArray(tierIDs)
              ? tierIDs.filter((x): x is string => typeof x === 'string')
              : []
            // Filters saved before Partner was renamed (MIL-402) carry the list as `partnerIDs`.
            const storedInsuranceCustomerAccountIDs: unknown =
              insuranceCustomerAccountIDs ?? (parsed as { partnerIDs?: unknown }).partnerIDs
            this.insuranceCustomerAccountIDs = Array.isArray(storedInsuranceCustomerAccountIDs)
              ? storedInsuranceCustomerAccountIDs.filter((x): x is string => typeof x === 'string')
              : []
            this.onlyReady = !!onlyReady
            return
          }
        }
      } catch {}

      this.applyDefaults()
    },

    persist() {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          tatIDs: this.tatIDs,
          tierIDs: this.tierIDs,
          insuranceCustomerAccountIDs: this.insuranceCustomerAccountIDs,
          onlyReady: this.onlyReady,
        } satisfies IDeliveriesFilters)
      )
    },

    setTatIDs(ids: string[]) {
      this.tatIDs = ids
      this.persist()
    },

    setTierIDs(ids: string[]) {
      this.tierIDs = ids
      this.persist()
    },

    setInsuranceCustomerAccountIDs(ids: string[]) {
      this.insuranceCustomerAccountIDs = ids
      this.persist()
    },

    setOnlyReady(value: boolean) {
      this.onlyReady = value
      this.persist()
    },

    applyDefaults() {
      this.tatIDs = [...DEFAULT_FILTERS.tatIDs]
      this.tierIDs = [...DEFAULT_FILTERS.tierIDs]
      this.insuranceCustomerAccountIDs = [...DEFAULT_FILTERS.insuranceCustomerAccountIDs]
      this.onlyReady = DEFAULT_FILTERS.onlyReady
    },

    clearFilters() {
      this.applyDefaults()
      this.persist()
    },
  },
})
